import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  Renderer2,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { ScriptLoaderService } from './common/script-loader.service';
import { BaseCtl, ServiceLocatorService, EventService } from '@ncs/ncs-lib';
import { SharedataService } from './common/service/sharedata.service';
import { TranslateService } from '@ngx-translate/core';
import { OverlayContainer } from '@angular/cdk/overlay';
declare var $: any;
declare var bootstrap: any;

interface MenuItem {
  label: string;
  url: string;
  icon: string;
  submenu?: MenuItem[]; // Optional submenu
}
// Group 1 - Dashboards
export const menus1: MenuItem[] = [
  { label: 'MENU.DASHBOARD', url: 'dashboard', icon: 'bi bi-house' },
  {
    label: 'MENU.APPS_DASHBOARD',
    url: 'appsdashboard',
    icon: 'bi bi-speedometer2',
  },
];

// Group 2 - App Management
export const menus2: MenuItem[] = [
  { label: 'MENU.APPLICATIONS', url: 'applicationlist', icon: 'bi bi-grid' },
  { label: 'MENU.RULES', url: 'rules', icon: 'bi bi-terminal-plus' },
  { label: 'MENU.UI_FORMS', url: 'uiallformlist', icon: 'bi bi-postcard' },
  { label: 'MENU.REPORTS', url: 'reportslist', icon: 'bi bi-clipboard2-data' },
];

// Group 3 - Users & Admin
export const menus3: MenuItem[] = [
  { label: 'MENU.APPS_STORE', url: 'appstore', icon: 'bx bx-store' },
  { label: 'MENU.APPS_PURCHASED', url: 'appbought', icon: 'bi bi-bag-check' },
  {
    label: 'MENU.APPS_REQUESTS',
    url: 'appstatus',
    icon: 'bi bi-cloud-arrow-down',
  },
];

// Group 4 - App Store
export const menus4: MenuItem[] = [
  {
    label: 'MENU.DEV_APPS',
    url: 'appcollaboratorlist',
    icon: 'bi bi-person-workspace',
  },
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseCtl {
  override title = 'NCSIndore';
  isPageLoading: boolean = false;
  loadingMessage: string = 'Loading';
  isBackToTopVisible = false;
  private isLoggingOut = false;
  private _loaderStartTime: number | null = null;
  private _messageInterval: any = null;
  currentYear: number = new Date().getFullYear();
  public menus1: MenuItem[] = menus1;
  public menus2: MenuItem[] = menus2;
  public menus3: MenuItem[] = menus3;
  public menus4: MenuItem[] = menus4;
  currentLang: string = 'en';
  public menuItems: any = [];
  public userContext: any = {};
  public ctxPath = '';
  public 'eventChangeMenu': Subscription;
  public 'eventSessionExpired': Subscription;
  public previousUrl = '';
  public 'eventRefresh': Subscription;

  observedValue: string | null = null;

  // Single source of truth: always reflects the active <html> class
  selectedTheme: string = 'light-theme';
  showLayout = true;
  isPublicRoute: boolean = false;
  private readonly THEME_CLASSES = ['light-theme', 'dark-theme', 'semi-dark'];
  private readonly PUBLIC_ROUTES = [
    '/login',
    '/signup',
    '/signupInvitation',
    '/forgot-password',
  ];

  // All sidebar-color-related classes — must never be stripped when theme changes
  private readonly SIDEBAR_COLOR_CLASSES = [
    'color-header',
    'color-sidebar',
    ...[
      'sidebarcolor1',
      'sidebarcolor2',
      'sidebarcolor3',
      'sidebarcolor4',
      'sidebarcolor5',
      'sidebarcolor6',
      'sidebarcolor7',
      'sidebarcolor8',
    ],
    ...[
      'headercolor1',
      'headercolor2',
      'headercolor3',
      'headercolor4',
      'headercolor5',
      'headercolor6',
      'headercolor7',
      'headercolor8',
    ],
  ];

  constructor(
    public override locator: ServiceLocatorService,
    public eService: EventService,
    public override route: ActivatedRoute,
    private router: Router,
    private scriptLoader: ScriptLoaderService,
    private renderer: Renderer2,
    private el: ElementRef,
    private shareService: SharedataService,
    private translate: TranslateService,
    private overlayContainer: OverlayContainer,
  ) {
    super('', locator, route);
    const savedLang = localStorage.getItem('userLanguage') || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang);
    this.currentLang = savedLang;

    // Before
    // this.router.events.subscribe((event) => {
    //   if (event instanceof NavigationEnd) {
    //     let currentUrl = event.urlAfterRedirects;
    //     if (currentUrl.startsWith('/#')) {
    //       currentUrl = currentUrl.substring(2);
    //     }
    //     this.showLayout = !currentUrl.startsWith('/form/');
    //   }
    // });
    // AFTER
    this.router.events.subscribe((event) => {
      // ── LOADER ────────────────────────────────────────────────────────────
      if (event instanceof NavigationStart) {
        // keySuffix from localStorage — always available synchronously
        const keySuffix = localStorage.getItem('keySuffix') || '';
        const isLoggedIn = !!localStorage.getItem(keySuffix + 'userContext');
        const isAuthUrl = this.PUBLIC_ROUTES.some((path) =>
          event.url.includes(path),
        );

        // Show loader on:
        // 1. Any browser refresh (F5 / Ctrl+R) on any page while logged in
        // 2. Any navigation to /dashboard (covers post-login redirect too)
        const isRefresh =
          event.id === 1 && !this.isLoggingOut && isLoggedIn && !isAuthUrl;
        const isDashboardNav =
          event.url.includes('/dashboard') && !this.isLoggingOut && isLoggedIn;

        if (isRefresh) {
          if (this._messageInterval) {
            clearInterval(this._messageInterval);
            this._messageInterval = null;
          }
          this.isPageLoading = true;
          this.loadingMessage = 'Loading';
          this._loaderStartTime = Date.now();

          this._messageInterval = setInterval(() => {
            if (!this.isPageLoading) return;
            if (this.loadingMessage === 'Loading') {
              this.loadingMessage = 'Initializing';
            } else if (this.loadingMessage === 'Initializing') {
              this.loadingMessage = 'Processing';
            } else if (this.loadingMessage === 'Processing') {
              this.loadingMessage = 'Finalizing';
            }
          }, 800);
        }
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        if (this.isPageLoading) {
          const navEndTime = Date.now();
          const startTime = this._loaderStartTime ?? navEndTime;
          const elapsed = navEndTime - startTime;
          const minDisplay = 2000;
          const remaining = Math.max(0, minDisplay - elapsed);

          setTimeout(() => {
            if (this._messageInterval) {
              clearInterval(this._messageInterval);
              this._messageInterval = null;
            }
            this.isPageLoading = false;
            this.loadingMessage = 'Loading';
            this._loaderStartTime = null;
          }, remaining);
        }
      }
      // ── END LOADER ────────────────────────────────────────────────────────

      if (event instanceof NavigationEnd) {
        let currentUrl = event.urlAfterRedirects;
        if (currentUrl.startsWith('/#')) {
          currentUrl = currentUrl.substring(2);
        }
        this.showLayout = !currentUrl.startsWith('/form/');

        // Check if current route is a public (no-theme) page
        const isPublic =
          this.PUBLIC_ROUTES.some((r) => currentUrl.startsWith(r)) ||
          currentUrl.startsWith('/form/');

        const html = document.documentElement;
        const overlay = this.overlayContainer.getContainerElement();

        if (isPublic) {
          // Strip all theme + sidebar/header color classes on public pages
          this.THEME_CLASSES.forEach((cls) => {
            html.classList.remove(cls);
            overlay.classList.remove(cls);
          });
          this.SIDEBAR_COLOR_CLASSES.forEach((cls) => {
            html.classList.remove(cls);
            overlay.classList.remove(cls);
          });
          this.isPublicRoute = true;
        } else if (this.isPublicRoute) {
          // Coming back from a public page — re-apply saved theme
          this.isPublicRoute = false;
          const savedTheme =
            localStorage.getItem('selectedTheme') || 'light-theme';
          this.selectedTheme = savedTheme;
          this.THEME_CLASSES.forEach((cls) => {
            html.classList.remove(cls);
            overlay.classList.remove(cls);
          });
          html.classList.add(savedTheme);
          overlay.classList.add(savedTheme);
        }
      }
    });

    this.locator.config
      .getProperty('endpoint.auth')
      .then((property: string) => {
        this.initApiEndpoint(property);
      });
  }

  override async ngOnInit() {
    // AppComponent ONLY sets selectedTheme here — never writes to <html> or
    // the overlay on init. ThemeCustomizerComponent.ngOnInit() is the sole
    // owner of DOM/overlay writes, and always runs AFTER this (child ngOnInit
    // fires after parent). We read localStorage as ground truth, with the
    // <html> class as a fallback.
    this.selectedTheme =
      localStorage.getItem('selectedTheme') ||
      this.THEME_CLASSES.find((cls) =>
        document.documentElement.classList.contains(cls),
      ) ||
      'light-theme';
    // No DOM writes here — ThemeCustomizer owns that.

    if (this.showLayout) {
      this.subscribeToData();
      this.profileName();
      this.updateProfile();

      this.locator.config
        .getProperty('CONTEXT_PATH')
        .then((property: string) => {
          this.initAppliation();
        });

      this.loadUserContextFromLocalStorage();
    }
  }

  loadUserContextFromLocalStorage() {
    const keySuffix = localStorage.getItem('keySuffix');
    if (keySuffix) {
      const storedUserContext = localStorage.getItem(`${keySuffix}userContext`);
      if (storedUserContext) {
        const data = JSON.parse(storedUserContext);
        this.userContext['imageId'] = data['imageId'];
      }
    }
  }

  // subscribeToData() {
  //   this.shareService.getSharedData().subscribe({
  //     next: (data) => {
  //       if (data['theme']) {
  //         this.selectedTheme = data['theme'];
  //         // Keep <html> and overlay in sync whenever theme changes via the service.
  //         // Only swap theme classes — sidebar/header color classes are untouched.
  //         const html = document.documentElement;
  //         const overlay = this.overlayContainer.getContainerElement();
  //         this.THEME_CLASSES.forEach((cls) => {
  //           html.classList.remove(cls);
  //           overlay.classList.remove(cls);
  //         });
  //         html.classList.add(this.selectedTheme);
  //         overlay.classList.add(this.selectedTheme);
  //       }
  //     },
  //     error: (err) => console.error('Error:', err),
  //   });
  // }
  // AFTER
  subscribeToData() {
    this.shareService.getSharedData().subscribe({
      next: (data) => {
        if (data['theme']) {
          this.selectedTheme = data['theme'];
          const html = document.documentElement;
          const overlay = this.overlayContainer.getContainerElement();

          // Remove theme classes AND sidebar/header color classes to stay
          // in sync with theme-customizer's behavior when theme is changed.
          this.THEME_CLASSES.forEach((cls) => {
            html.classList.remove(cls);
            overlay.classList.remove(cls);
          });
          this.SIDEBAR_COLOR_CLASSES.forEach((cls) => {
            html.classList.remove(cls);
            overlay.classList.remove(cls);
          });

          html.classList.add(this.selectedTheme);
          overlay.classList.add(this.selectedTheme);
        }
      },
      error: (err) => console.error('Error:', err),
    });
  }

  updateProfile() {
    this.shareService.getSharedData().subscribe({
      next: (data) => {
        if (data && data['imageId'] !== undefined && data['imageId'] !== null) {
          let keySuffix = localStorage.getItem('keySuffix');
          let storedUserContext = keySuffix
            ? localStorage.getItem(keySuffix + 'userContext')
            : null;
          let existingUserContext = storedUserContext
            ? JSON.parse(storedUserContext)
            : {};
          this.userContext = {
            ...existingUserContext,
            imageId: data['imageId'],
          };
          if (keySuffix) {
            localStorage.setItem(
              keySuffix + 'userContext',
              JSON.stringify(this.userContext),
            );
          }
        }
      },
      error: (err: any) => {
        console.error('Error in shared data subscription:', err);
      },
    });
  }

  profileName() {
    this.shareService.getSharedData().subscribe({
      next: (data) => {
        if (data['name']) {
          this.locator.userContext['name'] = data['name'];
          let keySuffix = localStorage.getItem('keySuffix');
          let storedUserContext = keySuffix
            ? localStorage.getItem(keySuffix + 'userContext')
            : null;
          let existingUserContext = storedUserContext
            ? JSON.parse(storedUserContext)
            : {};
          this.userContext = {
            ...existingUserContext,
            name: data['name'],
          };
          if (keySuffix) {
            localStorage.setItem(
              keySuffix + 'userContext',
              JSON.stringify(this.userContext),
            );
          }
        }
      },
      error: (err) => console.error('Error:', err),
    });
  }

  onImageError(event: Event, path: string) {
    const target = event.target as HTMLImageElement;
    target.src = path;
  }

  initAppliation() {
    let _self = this;
    _self.form['roleId'] = 0;

    this.ctxPath = _self.locator.config.get('CONTEXT_PATH');
    let keySuffix = this.ctxPath + '-';

    const fullUrl = window.location.href;
    const hashIndex = fullUrl.indexOf('#');
    let currentRoute =
      hashIndex >= 0 ? fullUrl.substring(hashIndex + 1).split('?')[0] : '';
    const isPublicForm = currentRoute.startsWith('/form/');

    if (_self.locator.userContext == null) {
      const uc = localStorage.getItem(keySuffix + 'userContext');
      if (uc) {
        _self.locator.userContext = JSON.parse(uc);
      } else {
        console.log('No userContext found:', _self.locator.userContext);
        if (!isPublicForm) {
          _self.locator.router.navigateByUrl('/login');
          return;
        }
      }
    }

    this.eventSessionExpired = this.eService.sessionExpired.subscribe(
      (text) => {
        this.isLoggingOut = true;
        this.isPageLoading = false;
        if (this._messageInterval) {
          clearInterval(this._messageInterval);
          this._messageInterval = null;
        }
        this.closeAllModals();
        this.menuItems = [];
        this.userContext = {};
        this.locator.userContext = null;
        const fullUrl = window.location.href;
        const hashIndex = fullUrl.indexOf('#');
        let route =
          hashIndex >= 0 ? fullUrl.substring(hashIndex + 1).split('?')[0] : '';
        if (!route.startsWith('/form/')) {
          this.router.navigate(['/login']);
        }
      },
    );

    this.eventRefresh = this.locator.router.events.subscribe((event: any) => {
      const keySuffix = _self.locator.config.get('CONTEXT_PATH') + '-';
      const language = localStorage.getItem('userLanguage') || 'en';
      this.translate.use(language);

      // ── MENU RESTORE ON REFRESH ───────────────────────────────────────────
      if (event instanceof NavigationStart) {
        if (this.menuItems.length === 0) {
          const userCtxStr = localStorage.getItem(keySuffix + 'userContext');
          const menuItemStr = localStorage.getItem(keySuffix + 'menuItem');
          if (userCtxStr) {
            this.menuItems = JSON.parse(menuItemStr ?? '[]');
            _self.userContext = JSON.parse(userCtxStr || '{}');
          }
        }
        this.previousUrl = this.locator.router.url;
      }
    });
  }

  closeAllModals() {
    const modals = document.querySelectorAll('.modal');
    modals.forEach((modal: Element) => {
      const htmlModal = modal as HTMLElement;
      this.renderer.removeClass(htmlModal, 'show');
      this.renderer.setAttribute(htmlModal, 'aria-hidden', 'true');
      this.renderer.setStyle(htmlModal, 'display', 'none');
    });
  }

  isActive(menuItem: any): boolean {
    return menuItem.url ? this.router.url === `/${menuItem.url}` : false;
  }

  isValidUser() {
    let _self = this;
    let keySuffix = this.ctxPath + '-';
    if (_self.locator.userContext != null) {
      if (_self.locator.userContext['name'] != null) {
        return true;
      }
    } else {
      const uc = localStorage.getItem(keySuffix + 'userContext');
      if (uc) {
        _self.locator.userContext = JSON.parse(uc);
        if (_self.locator.userContext['name'] != null) {
          return true;
        }
      }
    }
    return false;
  }

  getMenu() {
    let _self = this;
    let roleName: any = null;
    let keySuffix = _self.locator.config.get('CONTEXT_PATH') + '-';
    let appId = localStorage.getItem(keySuffix + 'appId');
    let roleId = localStorage.getItem(keySuffix + 'roleId');
    const obj = JSON.parse(
      localStorage.getItem(keySuffix + 'userContext') || '{}',
    );
    roleName = obj.roleName;
    this.form['roleId'] = _self.locator.dataValidator.toInt(roleId);
    _self.menuItems = [];

    if (!appId || !roleId) {
      return;
    }

    let url = this.locator.config.get('endpoint.auth') + 'sso/menu';
    url += '/' + +appId + '/' + roleId;

    this.locator.http.get(url, function (res: any, error: any) {
      if (error) {
        return;
      }
      localStorage.setItem(keySuffix + 'menuItem', JSON.stringify(res));
      _self.menuItems = res;

      if (roleName === 'Candidate') {
        _self.locator.router.navigateByUrl('/courselist');
      } else {
        _self.locator.router.navigateByUrl('/dashboard');
      }
    });
  }

  changeRole() {
    let _self = this;
    localStorage.setItem(
      _self.locator.config.get('CONTEXT_PATH') + 'roleId',
      this.form['roleId'] + '',
    );
    this.eService.menuChanged('');
    for (let i = 0; i < this.userContext['roles'].length; i++) {
      if (this.userContext['roles'][i].id == this.form['roleId']) {
        this.userContext['roleName'] = this.userContext['roles'][i].value;
      }
    }
  }

  gotoHome() {
    if (this.isValidUser()) {
      this.locator.router.navigateByUrl('/dashboard');
    } else {
      this.locator.router.navigateByUrl('/login');
    }
  }

  public searchTerm: string = '';
  public suggestions: string[] = [];
  public submenuList: any = [];

  onInputChange(): void {
    console.log(this.menuItems);
    if (this.submenuList.length === 0) {
      this.menuItems.forEach((element: any) => {
        this.submenuList.push(element.submenu);
      });
    }
    console.log('this.submenuList > ', this.submenuList);
  }

  selectSuggestion(suggestion: string): void {
    console.log(suggestion);
    this.searchTerm = suggestion;
    this.suggestions = [];
  }

  public nameValue: any;
  onNameChange(event: any) {
    this.nameValue = event.target.value;
    if (this.nameValue.length > 2) {
      console.log('this.nameValue ', this.nameValue);
    }
  }

  @Output() searchChange = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<string>();

  searchText: string = '';
  selectedFilter: string = '';

  onSearchChange() {
    this.searchChange.emit(this.searchText);
  }

  onFilterChange() {
    this.filterChange.emit(this.selectedFilter);
  }

  public searchTextNew = '';

  ngAfterViewInit() {
    var dropdownElementList = [].slice.call(
      document.querySelectorAll('.dropdown-toggle'),
    );
    var dropdownList = dropdownElementList.map(function (dropdownToggleEl) {
      return new bootstrap.Dropdown(dropdownToggleEl);
    });
    this.scriptLoader.load('assets/js/app.js');
  }

  isToggled = false;

  toggleSidebar() {
    const wrapper = this.el.nativeElement.querySelector('.wrapper');
    const sidebarWrapper =
      this.el.nativeElement.querySelector('.sidebar-wrapper');

    this.isToggled = !this.isToggled;

    if (this.isToggled) {
      this.renderer.addClass(wrapper, 'toggled');
      this.renderer.listen(sidebarWrapper, 'mouseenter', () => {
        this.renderer.addClass(wrapper, 'sidebar-hovered');
      });
      this.renderer.listen(sidebarWrapper, 'mouseleave', () => {
        this.renderer.removeClass(wrapper, 'sidebar-hovered');
      });
    } else {
      this.renderer.removeClass(wrapper, 'toggled');
    }
  }

  get filteredMenuItems(): MenuItem[] {
    const allMenus = [
      ...this.menus1,
      ...this.menus2,
      ...this.menus3,
      ...this.menus4,
    ];

    if (!this.searchText || this.searchText.trim() === '') {
      return allMenus;
    }

    const searchLower = this.searchText.toLowerCase();
    return allMenus.filter((item: MenuItem) => {
      const translatedLabel = this.translate.instant(item.label).toLowerCase();
      return translatedLabel.includes(searchLower);
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isBackToTopVisible = window.scrollY > 300;
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  onItemClick() {
    console.log('Menu item clicked!'); }

  private isThemeToggling = false;
  applyTheme() {
    if (this.isThemeToggling) return;
    this.isThemeToggling = true;

    this.selectedTheme =
      this.selectedTheme === 'dark-theme' || this.selectedTheme === 'semi-dark'
        ? 'light-theme'
        : 'dark-theme';

    const html = document.documentElement;
    const overlay = this.overlayContainer.getContainerElement();

    // Remove ALL theme classes AND sidebar/header color classes —
    // toggling the icon resets colors, same as theme-customizer's applyTheme()
    this.THEME_CLASSES.forEach((cls) => {
      html.classList.remove(cls);
      overlay.classList.remove(cls);
    });
    this.SIDEBAR_COLOR_CLASSES.forEach((cls) => {
      html.classList.remove(cls);
      overlay.classList.remove(cls);
    });

    html.classList.add(this.selectedTheme);
    overlay.classList.add(this.selectedTheme);

    localStorage.setItem('selectedTheme', this.selectedTheme);
    localStorage.removeItem('selectedSidebarColor'); // ← clear stored color too

    this.shareService.updateSharedData('theme', this.selectedTheme);

    setTimeout(() => (this.isThemeToggling = false), 300);
  }
  hasSidebarColor: boolean = false;
  get useWhiteLogo(): boolean {
    if (
      this.selectedTheme === 'dark-theme' ||
      this.selectedTheme === 'semi-dark'
    ) {
      return true;
    }
    // In light-theme, if any sidebar color is applied, sidebar is dark → use white logo
    const html = document.documentElement;
    return html.classList.contains('color-sidebar');
  }

  // ──────────────────────────────────────────────────────────────────────────

  clearSearchOnBlur() {
    setTimeout(() => {
      this.searchText = '';
    }, 300);
  }

  switchLanguage(language: string): void {
    this.translate.use(language);
    localStorage.setItem('userLanguage', language);
    this.currentLang = language;
  }

  openLink(url: string): void {
    window.open(url, '_blank');
  }
}
